// AddonsModal.jsx - shown to customers after they pick a service
// Beautiful upsell flow with images, descriptions, real-time price updates

import { useState, useEffect } from "react";
import { supabase } from "./supabase";

export default function AddonsModal({ service, onContinue, onBack }) {
  const [addons, setAddons] = useState([]);
  const [selected, setSelected] = useState({}); // { addon_id: true }
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!service?.id) { setLoading(false); return; }
    (async () => {
      const { data } = await supabase
        .from("service_addons")
        .select("*")
        .eq("service_id", service.id)
        .eq("is_active", true)
        .order("display_order")
        .order("id");
      setAddons(data || []);
      setLoading(false);
    })();
  }, [service?.id]);

  // If no add-ons or still loading, auto-skip
  useEffect(() => {
    if (!loading && addons.length === 0) {
      onContinue([]);
    }
  }, [loading, addons.length]);

  const toggle = (id) => setSelected(s => ({...s, [id]: !s[id]}));

  // Calculate totals
  const selectedAddons = addons.filter(a => selected[a.id]);
  const baseService = +service.price || 0;
  const addonsTotal = selectedAddons.reduce((sum, a) => sum + +(a.sale_price || a.regular_price), 0);
  const newTotal = baseService + addonsTotal;
  const totalSavings = selectedAddons.reduce((sum, a) => 
    a.sale_price ? sum + (+a.regular_price - +a.sale_price) : sum, 0);
  const extraDuration = selectedAddons.reduce((sum, a) => sum + (+a.duration_add_minutes || 0), 0);

  if (loading) {
    return (
      <div style={{padding:60,textAlign:"center",color:"#9ca3af",fontSize:13}}>
        Loading add-ons...
      </div>
    );
  }

  if (addons.length === 0) {
    // No add-ons - just skip
    return null;
  }

  const handleContinue = () => {
    const chosenAddons = selectedAddons.map(a => ({
      id: a.id,
      name: a.name,
      price: +(a.sale_price || a.regular_price),
      duration: +a.duration_add_minutes || 0,
    }));
    onContinue(chosenAddons);
  };

  return (
    <div style={{
      position: "fixed",
      inset: 0,
      background: "rgba(15,15,30,0.75)",
      zIndex: 5000,
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      padding: 20,
      backdropFilter: "blur(8px)",
      animation: "fadeIn 0.2s",
    }}>
      <style>{`
        @keyframes fadeIn { from { opacity: 0; } to { opacity: 1; } }
        @keyframes slideUp { from { transform: translateY(20px); opacity: 0; } to { transform: translateY(0); opacity: 1; } }
      `}</style>
      <div style={{
        background: "#fff",
        borderRadius: 20,
        maxWidth: 580,
        width: "100%",
        maxHeight: "92vh",
        overflow: "auto",
        boxShadow: "0 20px 60px rgba(0,0,0,0.3)",
        animation: "slideUp 0.3s",
      }}>
        {/* Hero header */}
        <div style={{
          padding: "32px 28px 22px",
          textAlign: "center",
          background: "linear-gradient(135deg, #faf7f2 0%, #f8f5f0 100%)",
          borderBottom: "1px solid #e8e4df",
        }}>
          <div style={{
            display: "inline-block",
            padding: "5px 14px",
            background: "rgba(180,140,60,0.12)",
            color: "#b48c3c",
            borderRadius: 50,
            fontSize: 10,
            fontWeight: 700,
            letterSpacing: "0.15em",
            textTransform: "uppercase",
            marginBottom: 14,
          }}>✨ Make It Even Better</div>
          <div style={{
            fontFamily: "'Playfair Display','Cormorant Garamond',Georgia,serif",
            fontSize: 28,
            fontWeight: 600,
            color: "#1a1a2e",
            lineHeight: 1.2,
            marginBottom: 6,
          }}>
            Enhance your {service.name}
          </div>
          <div style={{fontSize: 13, color: "#6b7280", lineHeight: 1.5}}>
            Get more from your treatment with these popular add-ons — chosen by our experts to amplify your results.
          </div>
        </div>

        {/* Add-ons list */}
        <div style={{padding: 18}}>
          {addons.map(a => (
            <AddonCard
              key={a.id}
              addon={a}
              isSelected={!!selected[a.id]}
              onToggle={() => toggle(a.id)}
            />
          ))}

          {/* Footer with totals */}
          <div style={{
            marginTop: 18,
            padding: 18,
            background: selectedAddons.length > 0 ? "linear-gradient(135deg,rgba(180,140,60,0.08),rgba(180,140,60,0.04))" : "#f8f5f0",
            border: "1.5px solid " + (selectedAddons.length > 0 ? "#b48c3c" : "#e8e4df"),
            borderRadius: 12,
            transition: "all 0.2s",
          }}>
            <div style={{display:"flex",justifyContent:"space-between",fontSize:13,color:"#6b7280",marginBottom:6}}>
              <span>{service.name}</span>
              <span>${baseService.toFixed(2)}</span>
            </div>
            {selectedAddons.length > 0 && (
              <>
                {selectedAddons.map(a => (
                  <div key={a.id} style={{display:"flex",justifyContent:"space-between",fontSize:12,color:"#b48c3c",marginBottom:4}}>
                    <span>+ {a.name}</span>
                    <span style={{fontWeight:600}}>${(+(a.sale_price || a.regular_price)).toFixed(2)}</span>
                  </div>
                ))}
                {totalSavings > 0 && (
                  <div style={{display:"flex",justifyContent:"space-between",fontSize:11,color:"#2e8b57",fontWeight:600,marginBottom:4}}>
                    <span>You're saving</span>
                    <span>−${totalSavings.toFixed(2)}</span>
                  </div>
                )}
              </>
            )}
            <div style={{
              display:"flex",
              justifyContent:"space-between",
              alignItems:"baseline",
              marginTop: 10,
              paddingTop: 10,
              borderTop: "1px solid rgba(180,140,60,0.2)",
            }}>
              <span style={{fontSize:14,fontWeight:700,color:"#1a1a2e"}}>Total</span>
              <span style={{fontSize:24,fontWeight:800,color:"#b48c3c",fontFamily:"'Playfair Display','Cormorant Garamond',Georgia,serif"}}>
                ${newTotal.toFixed(2)}
              </span>
            </div>
            {extraDuration > 0 && (
              <div style={{fontSize:10,color:"#9ca3af",marginTop:4,textAlign:"right"}}>
                +{extraDuration} min added to your treatment
              </div>
            )}
          </div>
        </div>

        {/* Buttons */}
        <div style={{
          padding: "16px 18px 22px",
          display: "flex",
          gap: 10,
          borderTop: "1px solid #e8e4df",
        }}>
          <button onClick={onBack} style={{
            padding: "13px 20px",
            background: "#fff",
            color: "#6b7280",
            border: "1px solid #e8e4df",
            borderRadius: 10,
            fontSize: 13,
            fontWeight: 600,
            cursor: "pointer",
            fontFamily: "'Montserrat',sans-serif",
          }}>
            ← Back
          </button>
          <button onClick={() => onContinue([])} style={{
            flex: 1,
            padding: "13px 20px",
            background: "#fff",
            color: "#1a1a2e",
            border: "1.5px solid #e8e4df",
            borderRadius: 10,
            fontSize: 13,
            fontWeight: 600,
            cursor: "pointer",
            fontFamily: "'Montserrat',sans-serif",
          }}>
            No thanks, continue
          </button>
          <button onClick={handleContinue} disabled={selectedAddons.length === 0} style={{
            flex: 1.4,
            padding: "13px 20px",
            background: selectedAddons.length === 0 ? "#e8e4df" : "linear-gradient(135deg,#b48c3c,#8a6f2e)",
            color: selectedAddons.length === 0 ? "#9ca3af" : "#fff",
            border: "none",
            borderRadius: 10,
            fontSize: 13,
            fontWeight: 700,
            cursor: selectedAddons.length === 0 ? "not-allowed" : "pointer",
            fontFamily: "'Montserrat',sans-serif",
            boxShadow: selectedAddons.length === 0 ? "none" : "0 4px 16px rgba(180,140,60,0.3)",
          }}>
            {selectedAddons.length === 0 ? "Add at least one" : `Continue with ${selectedAddons.length} add-on${selectedAddons.length > 1 ? "s" : ""} →`}
          </button>
        </div>
      </div>
    </div>
  );
}

function AddonCard({ addon, isSelected, onToggle }) {
  const hasDeal = addon.sale_price && +addon.sale_price < +addon.regular_price;
  const savings = hasDeal ? Math.round((1 - +addon.sale_price / +addon.regular_price) * 100) : 0;
  const displayPrice = hasDeal ? +addon.sale_price : +addon.regular_price;

  return (
    <div onClick={onToggle} style={{
      padding: 14,
      marginBottom: 10,
      background: isSelected ? "rgba(180,140,60,0.06)" : "#fff",
      border: "2px solid " + (isSelected ? "#b48c3c" : "#e8e4df"),
      borderRadius: 12,
      cursor: "pointer",
      transition: "all 0.18s",
      display: "flex",
      gap: 14,
      alignItems: "flex-start",
    }}>
      {addon.image_url ? (
        <img src={addon.image_url} alt={addon.name} style={{
          width: 80,
          height: 80,
          objectFit: "cover",
          borderRadius: 10,
          flexShrink: 0,
          border: "1px solid #e8e4df",
        }}/>
      ) : (
        <div style={{
          width: 80,
          height: 80,
          background: "linear-gradient(135deg,#f8f5f0,#e8e4df)",
          borderRadius: 10,
          flexShrink: 0,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          fontSize: 28,
        }}>✨</div>
      )}
      <div style={{flex: 1, minWidth: 0}}>
        <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-start",gap:10,marginBottom:4}}>
          <div style={{fontSize:15,fontWeight:700,color:"#1a1a2e",lineHeight:1.3}}>
            {addon.name}
          </div>
          <div style={{
            width: 26, height: 26, flexShrink: 0,
            borderRadius: "50%",
            border: "2px solid " + (isSelected ? "#b48c3c" : "#d1d5db"),
            background: isSelected ? "#b48c3c" : "#fff",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            color: "#fff",
            fontSize: 14,
            fontWeight: 800,
            transition: "all 0.15s",
          }}>
            {isSelected ? "✓" : ""}
          </div>
        </div>
        {addon.description && (
          <div style={{fontSize: 11, color: "#6b7280", lineHeight: 1.5, marginBottom: 8}}>
            {addon.description}
          </div>
        )}
        <div style={{display:"flex",alignItems:"center",gap:8,flexWrap:"wrap"}}>
          {hasDeal ? (
            <>
              <span style={{textDecoration:"line-through",color:"#9ca3af",fontSize:12}}>
                ${(+addon.regular_price).toFixed(0)}
              </span>
              <span style={{
                fontSize: 18,
                fontWeight: 800,
                color: "#b48c3c",
                fontFamily: "'Playfair Display','Cormorant Garamond',Georgia,serif",
              }}>
                ${displayPrice.toFixed(0)}
              </span>
              <span style={{
                fontSize: 9,
                fontWeight: 700,
                padding: "3px 8px",
                background: "rgba(46,139,87,0.12)",
                color: "#2e8b57",
                borderRadius: 50,
                textTransform: "uppercase",
                letterSpacing: "0.05em",
              }}>
                Save {savings}%
              </span>
            </>
          ) : (
            <span style={{
              fontSize: 18,
              fontWeight: 800,
              color: "#b48c3c",
              fontFamily: "'Playfair Display','Cormorant Garamond',Georgia,serif",
            }}>
              ${displayPrice.toFixed(0)}
            </span>
          )}
          {addon.duration_add_minutes > 0 && (
            <span style={{fontSize:10,color:"#9ca3af",marginLeft:4}}>
              +{addon.duration_add_minutes} min
            </span>
          )}
        </div>
      </div>
    </div>
  );
}
